import moment from "moment-business-days";
import { frequencyShortHandMap, listOfMonths } from "../constants.ts";
import { User } from "../types.ts";
import { convertCurrency } from "../requests/budget.ts";

export const formatAmount = (amount: number, currency: string) => {
  switch (currency) {
    case "USD":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    case "EUR":
      return new Intl.NumberFormat("en-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    case "JPY":
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
      }).format(amount);
    case "GBP":
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(amount);
    case "CHF":
      return new Intl.NumberFormat("de-CH", {
        style: "currency",
        currency: "CHF",
      }).format(amount);
    case "AUD":
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(amount);
    case "CAD":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "CAD",
      }).format(amount);
    default:
      return new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "USD",
      }).format(amount);
  }
};

export const getDateInfo = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = listOfMonths[date.getMonth()];

  return {
    currentYear: year,
    currentMonth: month,
  };
};

export const loggedInHomepage = (user: User | undefined) => {
  return user && user.hasBudget ? `/cash-flow` : "/onboarding";
};

export const capitalizePageTitle = (title: string) => {
  let pageTitle = title.replace("/", "");
  const firstLetter = pageTitle.charAt(0).toUpperCase();
  pageTitle = pageTitle.slice(1);

  return firstLetter.concat(pageTitle);
};

export const getCurrentPageName = (
  pathName: string,
  ownerPayPageTitle = "Owner's Pay",
) => {
  if (pathName === "/") {
    return {
      pageName: "Home",
      page2Name: "",
    };
  }

  const page = pathName.split("/");
  const path = page.length === 2 ? `/${page[1]}` : `/${page[1]}/${page[2]}`;
  let pageName = "";
  let page2Name = "";

  switch (path) {
    case "/onboarding":
      pageName = "Getting Started";
      break;
    case "/cash-flow":
      pageName = "Cash Flow";
      break;
    case "/income":
      pageName = "Income";
      page2Name = "Cash Flow";
      break;
    case "/owner-pay":
      pageName = ownerPayPageTitle;
      page2Name = "Cash Flow";
      break;
    case "/monthly/expense":
      pageName = "Monthly Expense";
      page2Name = "Cash Flow";
      break;
    case "/account":
      pageName = "Account";
      break;
    default:
      pageName = capitalizePageTitle(path);
      break;
  }

  return {
    pageName,
    page2Name,
  };
};

export const getErrorMessage = (label: string, amount: string | number) => {
  const msg: string[] = [];

  if (!label) {
    msg.push("Please enter a label");
  }

  if (!amount || amount === "0") {
    msg.push("Please enter an amount");
  }

  return msg;
};

export const getFrequencyValue = (
  value: number,
  month: string,
  year: number,
  frequency?: string,
) => {
  if (!frequency) {
    return value;
  }

  const businessDays = moment(
    `${year}-${listOfMonths.indexOf(month) + 1}-01`,
    "YYYY-MM-DD",
  ).monthBusinessDays().length;

  switch (frequency) {
    case "Daily":
      return Number((value * businessDays).toFixed(2));
    case "Weekly":
      return Number((value * 4).toFixed(2));
    case "Semi-Monthly":
      return Number((value * 2).toFixed(2));
    case "Monthly":
      return value;
    default:
      return value;
  }
};

export const getFrequencyContent = async (
  month?: string,
  year?: string,
  amount?: number,
  frequency?: string,
  currentUser?: User,
) => {
  if (!frequency || !month || !year) {
    return "every month";
  }

  const freq = frequencyShortHandMap(frequency);
  const val = revertAmountToOriginal(amount || 0, month, year, frequency);
  const { currencyValue } = await getFormattedCurrency(val, currentUser);

  return freq === "month"
    ? `Repeats monthly`
    : `${currencyValue} every ${freq}`;
};

export const revertAmountToOriginal = (
  value: number,
  month?: string,
  year?: string,
  frequency?: string,
) => {
  if (!frequency || !month || !year) {
    return value;
  }

  const businessDays = moment(
    `${year}-${listOfMonths.indexOf(month) + 1}-01`,
    "YYYY-MM-DD",
  ).monthBusinessDays().length;

  switch (frequency) {
    case "Daily":
      return value / businessDays;
    case "Weekly":
      return value / 4;
    case "Semi-Monthly":
      return value / 2;
    case "Monthly":
      return value;
    default:
      return value;
  }
};

export const getFormattedCurrency = async (
  amount: number,
  currentUser?: User,
) => {
  if (
    !currentUser ||
    (currentUser && currentUser.currency === "USD") ||
    !currentUser.currency
  ) {
    const convertedValue = formatAmount(amount, "USD");

    return {
      currencyValue: convertedValue,
      emptyValue: formatAmount(0, "USD"),
    };
  }

  const currencyRate = await convertCurrency("USD", currentUser.currency);
  const convertedValue = formatAmount(
    amount * currencyRate.rates[currentUser.currency],
    currentUser.currency,
  );

  return {
    currencyValue: convertedValue,
    emptyValue: formatAmount(0, currentUser.currency),
  };
};

export const checkIsExpiredSession = (err: any) => {
  return (
    err.error === "login_required" ||
    err.error === "consent_required" ||
    err.error === "invalid_grant" ||
    err.code === "auth/user-token-expired" ||
    err.code === "auth/invalid-user-token" ||
    err.code === "auth/user-disabled" ||
    err.code === "auth/user-not-found"
  );
};
